/**
 * Pulse Radio (WP4-RADIO, BUILD-PLAN M25; reuse guide
 * mailbox/to-treadmill/20261009-reuse-pulse-status-radio*.md). A Marketplace
 * add-on: Velora manages each office's zones, stations, players and overhead
 * announcements; the Pulse Radio player app (one per zone) plays.
 *
 * - **Zones** (reception, clinical, break room) each have a station, a volume
 *   and a mute. An office can be paused as a whole from any user and
 *   resumed from any user (Pulse Radio's master pause).
 * - **Stations** are licensed business-music streams. Each names the
 *   provider and the licence it plays under (guide section d): there is no
 *   YouTube search, no download of third-party audio and no offline save.
 * - **Players** enrol with a short-lived, hashed, max-use code and receive
 *   their own revocable device token. Long-lived invites are never baked
 *   into installers (guide sections c2.3 and e).
 * - **Announcements** play over the music in every zone or chosen zones.
 *   An emergency page plays even when announcements are turned off.
 *
 * Marketplace (M23): every user route answers 402 `entitlement_required`
 * without the `radio` entitlement; a player of a lapsed office is told
 * `ended` and stops on its next poll. Pulse Radio never reads PHI.
 */
import { z } from 'zod';

/** Marketplace feature key (entitlement row `feature_key`). */
export const RADIO_FEATURE = 'radio';

/**
 * Permission keys the module checks. Until dedicated `radio.*` keys exist in
 * the identity catalogue (docs/requests/radio-permissions.md), control rides
 * on `schedule.view` (everyone who works in the office), announcements on
 * `schedule.edit`, stations and office radio settings on `settings.manage`,
 * and players and enrolment codes on `devices.manage`.
 */
export const RADIO_PERMISSIONS = {
  view: 'schedule.view',
  control: 'schedule.view',
  announce: 'schedule.edit',
  manage: 'settings.manage',
  devices: 'devices.manage',
} as const;

/** SSE entity keys. Query keys in the web module start with these. */
export const RADIO_ENTITY = {
  zone: 'radio.zone',
  station: 'radio.station',
  device: 'radio.device',
  announcement: 'radio.announcement',
} as const;

/** Header a player sends its device token in. */
export const RADIO_DEVICE_HEADER = 'x-radio-device';

/** A player counts as online when it polled within this window. */
export const PLAYER_ONLINE_MS = 2 * 60 * 1000;
/** How often a player polls (it also polls right after an announcement ends). */
export const PLAYER_POLL_SECONDS = 15;
/** Default quiet hours (prototype: "Quiet hours 6 PM to 7 AM"). */
export const DEFAULT_QUIET_HOURS = { start: '18:00', end: '07:00' } as const;
/** Zones created by "Set up zones" (prototype preview). */
export const DEFAULT_ZONES = [
  { name: 'Reception', volume: 60 },
  { name: 'Clinical', volume: 35 },
  { name: 'Break room', volume: 50 },
] as const;
/** How long an announcement stays live for players to pick up. */
export const ANNOUNCEMENT_TTL_SECONDS = 120;

/* ------------------------------------------------------------------------ */
/* Primitives                                                                */
/* ------------------------------------------------------------------------ */

/** `HH:MM`, 24-hour. */
export const ClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'use HH:MM, 24-hour');
export type ClockTime = z.infer<typeof ClockTimeSchema>;

export const HttpsUrlSchema = z
  .string()
  .trim()
  .max(500)
  .regex(/^https:\/\/\S{3,}$/, 'must be an https:// address');

const Name = (max: number) => z.string().trim().min(1).max(max);

export const QuietHoursSchema = z
  .object({ start: ClockTimeSchema, end: ClockTimeSchema })
  .refine((q) => q.start !== q.end, { message: 'start and end must differ' });
export type QuietHours = z.infer<typeof QuietHoursSchema>;

/**
 * Why a zone is or is not playing, in precedence order:
 * `ended` (subscription lapsed) > `paused` (whole office) > `quiet_hours` >
 * `no_station` > `muted` > `playing`.
 */
export const PLAY_STATES = [
  'playing',
  'muted',
  'no_station',
  'quiet_hours',
  'paused',
  'ended',
] as const;
export const PlayStateSchema = z.enum(PLAY_STATES);
export type PlayState = z.infer<typeof PlayStateSchema>;

export const PLAY_STATE_LABELS: Readonly<Record<PlayState, string>> = {
  playing: 'Playing',
  muted: 'Muted',
  no_station: 'No station',
  quiet_hours: 'Quiet hours',
  paused: 'Paused',
  ended: 'Subscription ended',
};

export const AnnouncementKindSchema = z.enum(['page', 'emergency']);
export type AnnouncementKind = z.infer<typeof AnnouncementKindSchema>;

/* ------------------------------------------------------------------------ */
/* Views                                                                     */
/* ------------------------------------------------------------------------ */

export const StationSchema = z.object({
  id: z.uuid(),
  /** `null` = every office of the practice. */
  locationId: z.uuid().nullable(),
  name: z.string(),
  genre: z.string(),
  streamUrl: z.string(),
  licenceProvider: z.string(),
  licenceReference: z.string(),
  archived: z.boolean(),
  /** Zones currently set to it, across the offices in scope. */
  zoneCount: z.number().int().nonnegative(),
});
export type Station = z.infer<typeof StationSchema>;

export const ZoneSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  name: z.string(),
  stationId: z.uuid().nullable(),
  volume: z.number().int().min(0).max(100),
  muted: z.boolean(),
  primaryDeviceId: z.uuid().nullable(),
  sortOrder: z.number().int(),
  version: z.number().int(),
  /** Effective state for players right now. */
  state: PlayStateSchema,
  players: z.number().int().nonnegative(),
  playersOnline: z.number().int().nonnegative(),
});
export type Zone = z.infer<typeof ZoneSchema>;

export const DeviceSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  zoneId: z.uuid().nullable(),
  name: z.string(),
  appVersion: z.string().nullable(),
  enrolledAt: z.iso.datetime({ offset: true }),
  lastSeenAt: z.iso.datetime({ offset: true }).nullable(),
  online: z.boolean(),
  primary: z.boolean(),
});
export type Device = z.infer<typeof DeviceSchema>;

export const EnrolmentSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  label: z.string(),
  /** Last four characters, to tell codes apart. The code itself is shown once. */
  codeHint: z.string(),
  zoneId: z.uuid().nullable(),
  expiresAt: z.iso.datetime({ offset: true }),
  maxUses: z.number().int(),
  uses: z.number().int(),
  status: z.enum(['active', 'expired', 'used', 'revoked']),
  createdAt: z.iso.datetime({ offset: true }),
  createdByName: z.string(),
});
export type Enrolment = z.infer<typeof EnrolmentSchema>;

export const AnnouncementSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  kind: AnnouncementKindSchema,
  message: z.string(),
  zoneIds: z.array(z.uuid()).nullable(),
  createdAt: z.iso.datetime({ offset: true }),
  expiresAt: z.iso.datetime({ offset: true }),
  createdByName: z.string(),
});
export type Announcement = z.infer<typeof AnnouncementSchema>;

export const OfficeRadioSettingsSchema = z.object({
  announcementsEnabled: z.boolean(),
  quietHours: QuietHoursSchema.nullable(),
  pausedAt: z.iso.datetime({ offset: true }).nullable(),
  pausedByName: z.string().nullable(),
  playerUrl: z.string().nullable(),
});
export type OfficeRadioSettings = z.infer<typeof OfficeRadioSettingsSchema>;

export const RadioStatsSchema = z.object({
  zones: z.number().int().nonnegative(),
  playing: z.number().int().nonnegative(),
  players: z.number().int().nonnegative(),
  playersOnline: z.number().int().nonnegative(),
});
export type RadioStats = z.infer<typeof RadioStatsSchema>;

export const OfficeRadioSchema = z.object({
  locationId: z.uuid(),
  name: z.string(),
  timezone: z.string(),
  /** Office wall clock, `HH:MM`. */
  localTime: ClockTimeSchema,
  /** `read_only`: lapsed within the 90-day grace; players are stopped. */
  entitlement: z.enum(['active', 'read_only']),
  settings: OfficeRadioSettingsSchema,
  quietNow: z.boolean(),
  zones: z.array(ZoneSchema),
  devices: z.array(DeviceSchema),
  enrolments: z.array(EnrolmentSchema),
  announcements: z.array(AnnouncementSchema),
  stats: RadioStatsSchema,
});
export type OfficeRadio = z.infer<typeof OfficeRadioSchema>;

export const PlayerDownloadSchema = z.object({
  platform: z.enum(['windows', 'linux']),
  label: z.string(),
  detail: z.string(),
  /** `null` when this server has no installer configured. */
  url: z.string().nullable(),
});
export type PlayerDownload = z.infer<typeof PlayerDownloadSchema>;

export const RadioOverviewSchema = z.object({
  serverNow: z.iso.datetime({ offset: true }),
  offices: z.array(OfficeRadioSchema),
  /** Sums across offices (ratio of sums for rates). */
  combined: RadioStatsSchema,
  stations: z.array(StationSchema),
  /**
   * Whether this user may add, edit or archive stations shared by every
   * office (a manager of every active office of the practice).
   */
  canManageSharedStations: z.boolean(),
  downloads: z.array(PlayerDownloadSchema),
});
export type RadioOverview = z.infer<typeof RadioOverviewSchema>;

/* ------------------------------------------------------------------------ */
/* Requests                                                                  */
/* ------------------------------------------------------------------------ */

export const ZonePatchSchema = z
  .object({
    name: Name(40).optional(),
    stationId: z.uuid().nullable().optional(),
    volume: z.number().int().min(0).max(100).optional(),
    muted: z.boolean().optional(),
    primaryDeviceId: z.uuid().nullable().optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), {
    message: 'nothing to change',
  });
export type ZonePatch = z.infer<typeof ZonePatchSchema>;

export const ZoneCreateSchema = z.object({ name: Name(40) });
export type ZoneCreate = z.infer<typeof ZoneCreateSchema>;

export const StationInputSchema = z.object({
  /** `null` = every office of the practice. */
  locationId: z.uuid().nullable(),
  name: Name(60),
  genre: z.string().trim().max(40).default(''),
  streamUrl: HttpsUrlSchema,
  licenceProvider: z.string().trim().min(2).max(80),
  licenceReference: z.string().trim().min(2).max(80),
  /** The user confirms the stream is licensed for public performance. */
  attestLicensed: z.literal(true, { message: 'Confirm the station is licensed for business use' }),
});
export type StationInput = z.input<typeof StationInputSchema>;

export const StationPatchSchema = z
  .object({
    name: Name(60).optional(),
    genre: z.string().trim().max(40).optional(),
    streamUrl: HttpsUrlSchema.optional(),
    licenceProvider: z.string().trim().min(2).max(80).optional(),
    licenceReference: z.string().trim().min(2).max(80).optional(),
    archived: z.boolean().optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), {
    message: 'nothing to change',
  });
export type StationPatch = z.infer<typeof StationPatchSchema>;

export const SettingsPatchSchema = z
  .object({
    announcementsEnabled: z.boolean().optional(),
    quietHours: QuietHoursSchema.nullable().optional(),
    playerUrl: HttpsUrlSchema.nullable().optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), {
    message: 'nothing to change',
  });
export type SettingsPatch = z.infer<typeof SettingsPatchSchema>;

export const AnnounceRequestSchema = z.object({
  kind: AnnouncementKindSchema.default('page'),
  message: z.string().trim().min(1).max(200),
  /** `null` = every zone of the office. */
  zoneIds: z.array(z.uuid()).min(1).max(50).nullable().default(null),
});
export type AnnounceRequest = z.input<typeof AnnounceRequestSchema>;

export const EnrolmentCreateSchema = z.object({
  label: Name(60),
  zoneId: z.uuid().nullable().default(null),
  maxUses: z.number().int().min(1).max(200).default(1),
  days: z.number().int().min(1).max(30).default(7),
});
export type EnrolmentCreate = z.input<typeof EnrolmentCreateSchema>;

export const EnrolmentCreatedSchema = z.object({
  enrolment: EnrolmentSchema,
  /** Plain code, shown once. Format `XXXX-XXXX-XXXX`. */
  code: z.string(),
});
export type EnrolmentCreated = z.infer<typeof EnrolmentCreatedSchema>;

export const DevicePatchSchema = z
  .object({
    name: Name(60).optional(),
    zoneId: z.uuid().nullable().optional(),
  })
  .refine((p) => p.name !== undefined || p.zoneId !== undefined, {
    message: 'nothing to change',
  });
export type DevicePatch = z.infer<typeof DevicePatchSchema>;

/* ------------------------------------------------------------------------ */
/* Player protocol (public; device token in RADIO_DEVICE_HEADER)             */
/* ------------------------------------------------------------------------ */

/** Enrolment codes are 12 characters from an unambiguous alphabet. */
export const ENROL_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const EnrolCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((s) => s.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(new RegExp(`^[${ENROL_CODE_ALPHABET}]{12}$`), 'invalid code'));

export const PlayerEnrolRequestSchema = z.object({
  code: EnrolCodeSchema,
  name: Name(60),
  appVersion: z.string().trim().max(40).optional(),
});
export type PlayerEnrolRequest = z.input<typeof PlayerEnrolRequestSchema>;

export const PlayerEnrolResponseSchema = z.object({
  deviceId: z.uuid(),
  /** Device token. Store it; it is never shown again. */
  token: z.string(),
  officeName: z.string(),
  pollSeconds: z.number().int(),
});
export type PlayerEnrolResponse = z.infer<typeof PlayerEnrolResponseSchema>;

export const PlayerSyncRequestSchema = z.object({
  appVersion: z.string().trim().max(40).optional(),
});

export const PlayerStateSchema = z.object({
  deviceId: z.uuid(),
  deviceName: z.string(),
  officeName: z.string(),
  zone: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  state: PlayStateSchema,
  /** True when this player should output audio (state playing and primary or no primary). */
  audible: z.boolean(),
  volume: z.number().int().min(0).max(100),
  station: z
    .object({ id: z.uuid(), name: z.string(), genre: z.string(), streamUrl: z.string() })
    .nullable(),
  announcements: z.array(
    z.object({
      id: z.uuid(),
      kind: AnnouncementKindSchema,
      message: z.string(),
      createdAt: z.string(),
      expiresAt: z.string(),
      by: z.string(),
    }),
  ),
  pollSeconds: z.number().int(),
  serverNow: z.iso.datetime({ offset: true }),
});
export type PlayerState = z.infer<typeof PlayerStateSchema>;
